import { useCallback, useEffect, useRef, useState } from "react";
import { Search, FileText, BookOpen, GraduationCap, Users, Files, LibraryBig, Library, X } from "lucide-react";
import { apiUrl, authFetch } from "../../utils/api";
import { getActiveBusinessId } from "../../utils/business";
import { setDeepLinkTarget } from "../../utils/deepLink";

// Kategorie -> Deep-Link-Typ, den das Zielmodul beim Öffnen ausliest.
const DEEP_TYPE: Record<string, string> = {
  va: "va",
  qmhandbuch: "qmh",
  wiki: "wiki",
  fortbildungen: "training",
  mitarbeitende: "member",
  dokumente: "document",
  vorlagen: "template",
};

type SearchItem = { id: number | string; title: string; subtitle?: string | null };
type SearchGroup = { category: string; items: SearchItem[] };

type CategoryMeta = { label: string; tab: string; icon: typeof FileText };

// Kategorie -> Anzeigename, Ziel-Tab und Icon. Reihenfolge = Anzeige-Reihenfolge.
const CATEGORY_META: Record<string, CategoryMeta> = {
  va: { label: "Verfahrensanweisungen", tab: "va", icon: FileText },
  qmhandbuch: { label: "QM-Handbuch", tab: "qmhandbuch", icon: BookOpen },
  wiki: { label: "Unternehmenswiki", tab: "wiki", icon: Library },
  fortbildungen: { label: "Fortbildungen", tab: "fortbildungen", icon: GraduationCap },
  mitarbeitende: { label: "Mitarbeitende", tab: "mitarbeitende", icon: Users },
  dokumente: { label: "Dokumente", tab: "dokumente", icon: Files },
  vorlagen: { label: "Vorlagen", tab: "vorlagenbibliothek", icon: LibraryBig },
};
const CATEGORY_ORDER = Object.keys(CATEGORY_META);

type Props = {
  open: boolean;
  onClose: () => void;
  onNavigate: (tab: string) => void;
  /** Nur Kategorien anzeigen, deren Ziel-Tab die Rolle sehen darf. */
  allowedTabs: string[];
};

export function GlobalSearch({ open, onClose, onNavigate, allowedTabs }: Props) {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Beim Öffnen zurücksetzen und Input fokussieren.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setGroups([]);
    setSearched(false);
    const id = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(id);
  }, [open]);

  // Escape schließt.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const runSearch = useCallback(async (term: string) => {
    const businessId = getActiveBusinessId();
    if (businessId == null) { setGroups([]); setSearched(true); return; }
    setLoading(true);
    try {
      const response = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/search?q=${encodeURIComponent(term)}`));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, []);

  // Debounce.
  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setGroups([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    const id = window.setTimeout(() => void runSearch(term), 250);
    return () => window.clearTimeout(id);
  }, [query, open, runSearch]);

  if (!open) return null;

  const visibleGroups = CATEGORY_ORDER
    .map((category) => groups.find((group) => group.category === category))
    .filter((group): group is SearchGroup => !!group && group.items.length > 0)
    .filter((group) => allowedTabs.includes(CATEGORY_META[group.category].tab));

  const totalHits = visibleGroups.reduce((sum, group) => sum + group.items.length, 0);

  const go = (tab: string) => { onNavigate(tab); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/50 p-4 pt-[10vh]" onClick={onClose}>
      <div
        className="flex max-h-[75vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-4">
          <Search className="h-5 w-5 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Suchen: Verfahrensanweisungen, QM-Handbuch, Mitarbeitende, Fortbildungen…"
            className="flex-1 bg-transparent py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <button
            type="button"
            aria-label="Schließen"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Mindestens 2 Zeichen eingeben…</div>
          ) : loading && !searched ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Wird gesucht…</div>
          ) : totalHits === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">Keine Treffer für „{query.trim()}".</div>
          ) : (
            <div className="py-2">
              {visibleGroups.map((group) => {
                const meta = CATEGORY_META[group.category];
                const Icon = meta.icon;
                return (
                  <div key={group.category} className="px-2 py-1">
                    <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{meta.label}</div>
                    {group.items.map((item) => (
                      <button
                        key={`${group.category}-${item.id}`}
                        type="button"
                        onClick={() => {
                          const deepType = DEEP_TYPE[group.category];
                          if (deepType) setDeepLinkTarget(deepType, item.id);
                          go(meta.tab);
                        }}
                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-slate-100"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-slate-800">{item.title}</span>
                          {item.subtitle && <span className="block truncate text-xs text-slate-500">{item.subtitle}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-2 text-xs text-slate-400">
          <span>{totalHits > 0 ? `${totalHits} Treffer` : "Globale Suche"}</span>
          <span>Esc zum Schließen</span>
        </div>
      </div>
    </div>
  );
}
